import React, { useState, useEffect } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { I18nProvider, useI18n } from './context/I18nContext';
import Login from './components/Login';
import OnlinePlayers from './components/OnlinePlayers';
import PlayerLookup from './components/PlayerLookup';
import PhoneLookup from './components/PhoneLookup';
import Economy from './components/Economy';
import TransactionOverview from './components/TransactionOverview';
import DonateCodeGenerator from './components/DonateCodeGenerator';
import ItemSearch from './components/ItemSearch';
import WeaponLookup from './components/WeaponLookup';
import VehicleLookup from './components/VehicleLookup';
import TopVehicleModels from './components/TopVehicleModels';
import CharacterManagement from './components/CharacterManagement';
import UserManagement from './components/UserManagement';
import JobsManagement from './components/JobsManagement';
import SiteManagement from './components/SiteManagement';
import Profile from './components/Profile';
import AccountSettings from './components/AccountSettings';
import QueriesManagement from './components/QueriesManagement';
import AuditLog from './components/AuditLog';
import HousingLookup from './components/HousingLookup';
import BillingManagement from './components/BillingManagement';
import BossMenu from './components/BossMenu';
import HeistManagement from './components/HeistManagement';
import Refund from './components/Refund';
import RefundQueueManagement from './components/RefundQueueManagement';

function AppContent() {
  const { user, loading, logout, isAuthenticated, hasPermission, hasRole, siteId, setSiteId, apiFetch } = useAuth();
  const { t, language, setLanguage } = useI18n();
  const [activeTab, setActiveTab] = useState('online');
  const [vehicleLookupRequest, setVehicleLookupRequest] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [phoneLookupRequest, setPhoneLookupRequest] = useState(null);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });
  // Force V2 UI only
  const uiVersion = 'v2';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openNavGroups, setOpenNavGroups] = useState({});
  const [activeSubpageByTab, setActiveSubpageByTab] = useState({});

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };
  // UI version toggle removed; UI fixed to V2

  const toggleNavGroup = (groupId) => {
    setOpenNavGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const openChangePassword = () => {
    setShowChangePassword(true);
  };

  const [availableSites, setAvailableSites] = useState([]);

  useEffect(() => {
    const loadSites = async () => {
      try {
        const res = await apiFetch('/api/sites');
        const data = await res.json();
        if (res.ok && data.sites) {
          setAvailableSites(data.sites);
          // Auto-select when the user only has access to one site and hasn't selected a site yet
          if (Array.isArray(data.sites) && data.sites.length === 1 && (siteId === 'default' || !siteId)) {
            const single = data.sites[0].site_id;
            setSiteId(single);
            localStorage.setItem('adminSiteId', single);
          }
        }
      } catch (e) {
        // ignore
      }
    };
    loadSites();
    // refresh when user changes
  }, [apiFetch, user, setSiteId, siteId]);

  const openVehicleLookupByPlate = (plate) => {
    const normalizedPlate = String(plate || '').trim();
    if (!normalizedPlate) return;

    setVehicleLookupRequest({
      searchType: 'plate',
      searchValue: normalizedPlate,
      requestId: Date.now()
    });
    setActiveTab('vehicles');
    setActiveSubpageByTab((prev) => ({
      ...prev,
      vehicles: 'lookup'
    }));
  };

  const openPhoneLookupByNumber = (phoneNumber) => {
    const normalizedPhoneNumber = String(phoneNumber || '').trim();
    if (!normalizedPhoneNumber) return;

    setPhoneLookupRequest({
      phoneNumber: normalizedPhoneNumber,
      requestId: Date.now()
    });
    setActiveTab('phones');
    setActiveSubpageByTab((prev) => ({
      ...prev,
      phones: 'overview'
    }));
    setSidebarOpen(false);
  };

  const formatRole = (role) => {
    const labels = {
      root: t('app.role.root'),
      admin: t('app.role.admin'),
      moderator: t('app.role.moderator'),
      viewer: t('app.role.viewer')
    };

    return labels[role] || role;
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="App loading-screen">
        <div className="loading-content">
          <div className="loading-spinner-large"></div>
          <p>{t('app.loading')}</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  const tabs = [
    {
      id: 'online',
      label: `${t('app.tabs.online')}`,
      permission: 'players.view',
      subpages: [
        { id: 'online', label: language === 'da' ? 'Online spillere' : 'Online players' }
      ],
      requireSite: true,
      render: () => <OnlinePlayers onLookupVehicle={openVehicleLookupByPlate} onLookupPhone={openPhoneLookupByNumber} />
    },
    {
      id: 'lookup',
      label: `${t('app.tabs.lookup')}`,
      permission: 'players.view',
      subpages: [
        { id: 'search', label: language === 'da' ? 'Spillersøgning' : 'Player search' }
      ],
      requireSite: true,
      render: () => <PlayerLookup onLookupVehicle={openVehicleLookupByPlate} onLookupPhone={openPhoneLookupByNumber} />
    },
    {
      id: 'phones',
      label: language === 'da' ? 'Telefoner' : 'Phone lookup',
      permission: 'players.view',
      subpages: [
        { id: 'overview', label: language === 'da' ? 'Telefon opslag' : 'Phone overview' }
      ],
      requireSite: true,
      render: () => <PhoneLookup lookupPhoneRequest={phoneLookupRequest} onLookupPhone={openPhoneLookupByNumber} />
    },
    {
      id: 'economy',
      label: `${t('app.tabs.economy')}`,
      permission: 'economy.view',
      subpages: [
        { id: 'leaderboard', label: language === 'da' ? 'Topliste' : 'Leaderboard' },
        { id: 'manage', label: language === 'da' ? 'Administrer penge' : 'Manage money', permission: 'economy.edit' },
        { id: 'transactions', label: language === 'da' ? 'Transaktionsoversigt' : 'Transaction Overview' }
      ],
      requireSite: true,
      render: ({ subpage, showInternalNav = true } = {}) => {
        if (subpage === 'transactions') {
          return <TransactionOverview />;
        }
        return <Economy initialSection={subpage} showInternalNav={showInternalNav} />;
      }
    },
    {
      id: 'donate',
      label: `${t('app.tabs.donate')}`,
      permission: 'donate.view',
      requireSite: true,
      subpages: [
        { id: 'overview', label: language === 'da' ? 'Overblik' : 'Overview' }
      ],
      render: () => <DonateCodeGenerator />
    },
    {
      id: 'bossmenu',
      label: language === 'da' ? 'Virksomhedsøkonomi' : 'Company Finance',
      permission: 'companies.view',
      requireSite: true,
      subpages: [
        { id: 'management', label: language === 'da' ? 'Virksomhedsøkonomi' : 'Company Economy' }
      ],
      render: () => <BossMenu />
    },
    {
      id: 'refund',
      label: language === 'da' ? 'Refund System' : 'Refund System',
      permission: 'refund.view',
      requireSite: true,
      subpages: [
        { id: 'management', label: language === 'da' ? 'Refund System' : 'Refund System', permission: 'refund.edit' },
        { id: 'queue', label: language === 'da' ? 'Refund Queue' : 'Refund Queue', permission: 'refund.view' }
      ],
      render: ({ subpage }) => subpage === 'queue' ? <RefundQueueManagement /> : <Refund />
    },
    {
      id: 'items',
      label: `${t('app.tabs.items')}`,
      permission: 'items.view',
      subpages: [
        { id: 'search', label: language === 'da' ? 'Itemsøgning' : 'Item search' }
      ],
      requireSite: true,
      render: () => <ItemSearch />
    },
    {
      id: 'jobs',
      label: `${t('app.tabs.jobs')}`,
      permission: 'jobs.view',
      requireSite: true,
      subpages: [
        { id: 'management', label: language === 'da' ? 'Spillerjobs' : 'Player Jobs', permission: 'jobs.view' },
        { id: 'catalog', label: language === 'da' ? 'Jobs katalog' : 'Jobs Catalog', permission: 'jobs.edit' }
      ],
      render: ({ subpage }) => <JobsManagement subpage={subpage} />
    },
    {
      id: 'weapons',
      label: `${t('app.tabs.weapons')}`,
      permission: 'items.view',
      subpages: [
        { id: 'lookup', label: language === 'da' ? 'Våbensøgning' : 'Weapon lookup' }
      ],
      requireSite: true,
      render: () => <WeaponLookup />
    },
    {
      id: 'vehicles',
      label: `${t('app.tabs.vehicles')}`,
      permission: 'vehicles.view',
      subpages: [
        { id: 'lookup', label: language === 'da' ? 'Opslag via ejer/plade' : 'Lookup by owner/plate' },
        { id: 'model', label: language === 'da' ? 'Søg via model/hash' : 'Search by model/hash' }
      ],
      requireSite: true,
      render: ({ subpage, showInternalNav = true } = {}) => <VehicleLookup lookupRequest={vehicleLookupRequest} initialTab={subpage} showInternalNav={showInternalNav} />
    },
    {
      id: 'topvehicles',
      label: language === 'da' ? 'Populære køretøjer' : 'Popular Vehicles',
      permission: 'vehicles.view',
      subpages: [
        { id: 'top', label: language === 'da' ? 'Top køretøjer' : 'Top vehicles' }
      ],
      requireSite: true,
      render: () => <TopVehicleModels />
    },
    {
      id: 'character',
      label: `${t('app.tabs.character')}`,
      permission: 'characters.view',
      subpages: [
        { id: 'ped', label: language === 'da' ? 'Sæt ped model' : 'Set ped model', permission: 'characters.edit' },
        { id: 'move', label: language === 'da' ? 'Flyt karakter' : 'Move character', permission: 'characters.move' },
        { id: 'delete', label: language === 'da' ? 'Slet karakter' : 'Delete character', permission: 'characters.delete' }
      ],
      requireSite: true,
      render: ({ subpage, showInternalNav = true } = {}) => <CharacterManagement initialSection={subpage} showInternalNav={showInternalNav} />
    },
    {
      id: 'housing',
      label: `${t('app.tabs.housing')}`,
      permission: 'players.view',
      subpages: [
        { id: 'search', label: language === 'da' ? 'Husopslag' : 'Housing lookup' }
      ],
      requireSite: true,
      render: () => <HousingLookup />
    },

    {
      id: 'billing',
      label: language === 'da' ? 'Fakturering' : 'Billing',
      permission: 'economy.view',
      subpages: [
        { id: 'management', label: language === 'da' ? 'okokbilling' : 'okokbilling' }
      ],
      requireSite: true,
      render: () => <BillingManagement />
    },
    {
      id: 'users',
      label: `${t('app.tabs.users')}`,
      permission: 'users.view',
      subpages: [
        { id: 'management', label: language === 'da' ? 'Brugerhåndtering' : 'User management' }
      ],
      render: () => <UserManagement />
    },
    {
      id: 'profile',
      label: 'Profile',
      permission: null,
      render: () => <Profile />
    },
    {
      id: 'sites',
      label: `${t('app.tabs.sites') || 'Sites'}`,
      permission: null,
      // Only show Sites tab when logged into management (siteId === 'default')
      isVisible: () => (user && user.siteId === 'default') && (hasRole('root') || hasRole('admin') || hasRole('owner')),
      subpages: [
        { id: 'management', label: language === 'da' ? 'Server management' : 'Server management' }
      ],
      render: () => <SiteManagement />
    },
    {
      id: 'queries',
      label: `${t('app.tabs.queries') || 'Queries'}`,
      permission: null,
      requireSite: true,
      // Owner-only page for site-specific SQL template overrides
      isVisible: () => isSiteContext && user?.role === 'owner',
      subpages: [
        { id: 'management', label: language === 'da' ? 'Site queries' : 'Site queries' }
      ],
      render: () => <QueriesManagement />
    },
    {
      id: 'audit',
      label: `${t('app.tabs.audit')}`,
      permission: 'audit.view',
      subpages: [
        { id: 'logs', label: language === 'da' ? 'Audit-log' : 'Audit logs' }
      ],
      render: () => <AuditLog />
    },
    {
      id: 'heists',
      label: language === 'da' ? 'Heist' : 'Heist',
      permission: 'heists.manage',
      requireSite: true,
      subpages: [
        { id: 'management', label: language === 'da' ? 'Heist Håndtering' : 'Heist Management' }
      ],
      render: () => <HeistManagement />
    },
  ];

  const isSiteContext = !!(user && user.siteId && user.siteId !== 'default');

  const visibleTabs = tabs.filter((tab) => {
    if (tab.requireSite && !isSiteContext) return false;
    if (typeof tab.isVisible === 'function') {
      return tab.isVisible();
    }
    if (tab.permission) {
      return hasPermission(tab.permission);
    }
    return true;
  });
  const resolvedActiveTab = visibleTabs.find(tab => tab.id === activeTab)?.id || visibleTabs[0]?.id;
  const activeTabConfig = visibleTabs.find(tab => tab.id === resolvedActiveTab);

  const getVisibleSubpages = (tab) => {
    const raw = Array.isArray(tab?.subpages) && tab.subpages.length > 0
      ? tab.subpages
      : [{ id: 'default', label: tab?.label || tab?.id }];

    const filtered = raw.filter((subpage) => !subpage.permission || hasPermission(subpage.permission));
    return filtered.length > 0 ? filtered : [{ id: 'default', label: tab?.label || tab?.id }];
  };

  const getResolvedSubpageForTab = (tab) => {
    const visibleSubpages = getVisibleSubpages(tab);
    const selected = activeSubpageByTab[tab.id];
    const found = visibleSubpages.find((subpage) => subpage.id === selected);
    return found ? found.id : visibleSubpages[0].id;
  };

  const resolvedActiveSubpage = activeTabConfig ? getResolvedSubpageForTab(activeTabConfig) : null;

  const tabMap = visibleTabs.reduce((acc, tab) => {
    acc[tab.id] = tab;
    return acc;
  }, {});


  const groupedNavConfigs = [
    {
      id: 'players',
      label: language === 'da' ? 'Spillere' : 'Players',
      tabIds: ['online', 'lookup', 'phones', 'housing', 'jobs']
    },
    {
      id: 'economy-group',
      label: language === 'da' ? 'Økonomi' : 'Economy',
      tabIds: ['economy', 'donate', 'bossmenu', 'refund', 'billing']
    },
    {
      id: 'assets',
      label: language === 'da' ? 'Assets' : 'Assets',
      tabIds: ['vehicles', 'topvehicles', 'weapons', 'items']
    },
    {
      id: 'admin-group',
      label: language === 'da' ? 'Administration' : 'Administration',
      tabIds: ['character', 'users', 'audit', 'sites', 'queries', 'heists']
    }
  ];

  const visibleNavGroups = groupedNavConfigs
    .map((group) => ({
      ...group,
      tabs: group.tabIds.map((tabId) => tabMap[tabId]).filter(Boolean)
    }))
    .filter((group) => group.tabs.length > 0);

  const selectNavSubpage = (tabId, subpageId) => {
    setActiveTab(tabId);
    setActiveSubpageByTab((prev) => ({
      ...prev,
      [tabId]: subpageId
    }));
    setSidebarOpen(false);
  };

  const changePasswordModal = (
    <>
      {showChangePassword && (
        <AccountSettings onClose={() => setShowChangePassword(false)} />
      )}
    </>
  );

  if (uiVersion === 'v2') {
    return (
      <div className={`App app-v2 ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <button
          className="sidebar-toggle-mobile"
          onClick={() => setSidebarOpen((prev) => !prev)}
          title={language === 'da' ? 'Skift sidepanel' : 'Toggle sidebar'}
        >
          Menu
        </button>

        {/* LEFT SIDEBAR / MAIN NAV - Start here to edit the left navigation menu and groups */}
        <aside className="v2-sidebar">
          <div className="v2-brand">
            <span className="logo-icon">V</span>
            <div className="v2-brand-text">
              <h1>Void</h1>
              <p>{t('app.subtitle')}</p>
            </div>
          </div>

          <div className="v2-nav-groups">
            {visibleNavGroups.map((group) => (
              <div key={group.id} className="v2-nav-group">
                <button
                  className="v2-group-trigger"
                  onClick={() => toggleNavGroup(group.id)}
                >
                  <span>{group.label}</span>
                  <span className={`v2-group-caret ${(openNavGroups[group.id] ?? true) ? 'open' : ''}`}>▾</span>
                </button>
                {(openNavGroups[group.id] ?? true) && (
                  <div className="v2-group-items">
                    {group.tabs.map((tab) => {
                      const tabSubpages = getVisibleSubpages(tab);
                      const tabKey = `tab:${tab.id}`;

                      if (tabSubpages.length <= 1) {
                        const onlySubpage = tabSubpages[0];
                        return (
                          <button
                            key={`${group.id}:${tab.id}:${onlySubpage.id}`}
                            className={`v2-nav-item ${resolvedActiveTab === tab.id ? 'active' : ''}`}
                            onClick={() => selectNavSubpage(tab.id, onlySubpage.id)}
                          >
                            {tab.label}
                          </button>
                        );
                      }

                      return (
                        <div key={`${group.id}:${tab.id}`} className="v2-subgroup">
                          <button
                            className="v2-subgroup-trigger"
                            onClick={() => toggleNavGroup(tabKey)}
                          >
                            <span>{tab.label}</span>
                            <span className={`v2-group-caret ${(openNavGroups[tabKey] ?? (resolvedActiveTab === tab.id)) ? 'open' : ''}`}>▾</span>
                          </button>
                          {(openNavGroups[tabKey] ?? (resolvedActiveTab === tab.id)) && (
                            <div className="v2-subgroup-items">
                              {tabSubpages.map((subpage) => (
                                <button
                                  key={`${group.id}:${tab.id}:${subpage.id}`}
                                  className={`v2-nav-item v2-nav-item-sub ${resolvedActiveTab === tab.id && getResolvedSubpageForTab(tab) === subpage.id ? 'active' : ''}`}
                                  onClick={() => selectNavSubpage(tab.id, subpage.id)}
                                >
                                  {subpage.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        <main className="v2-main">
          <header className="v2-topbar">
            <div className="v2-user-row">
              <div className="user-info">
                <span className="user-avatar">{user?.username?.charAt(0).toUpperCase()}</span>
                <div className="user-details">
                  <span className="user-name">{user?.display_name || user?.username}</span>
                  <span className="user-role">{formatRole(user?.role)}</span>
                </div>
              </div>

              <div className="v2-actions">
                {availableSites.length > 1 && (
                  <select className="site-select-header" value={siteId} onChange={(e) => { setSiteId(e.target.value); localStorage.setItem('adminSiteId', e.target.value); }} title={t('I18n.siteId') || 'Site'}>
                    <option value="default">Default</option>
                    {availableSites.map(s => (
                      <option key={s.site_id} value={s.site_id}>{s.name || s.site_id}</option>
                    ))}
                  </select>
                )}
                <button
                  className="theme-toggle"
                  onClick={toggleTheme}
                  title={t('app.switchTheme', { theme: theme === 'dark' ? t('app.light') : t('app.dark') })}
                >
                  {theme === 'dark' ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                      <path d="M6.76 4.84l-1.8-1.79L3.17 4.85l1.79 1.79 1.8-1.8zM1 13h3v-2H1v2zm10-9h2V1h-2v3zm7.03 1.05l1.79-1.79-1.79-1.8-1.8 1.8 1.8 1.79zM17.24 19.16l1.8 1.79 1.79-1.8-1.79-1.79-1.8 1.8zM20 13h3v-2h-3v2zM12 7a5 5 0 100 10 5 5 0 000-10zM6.76 19.16l-1.8 1.79L3.17 19.16l1.79-1.79 1.8 1.79z" fill="currentColor" />
                    </svg>
                  )}
                </button>
                <div className="language-switcher">
                  <select
                    className="language-select-header"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    title="Language"
                    aria-label="Language"
                  >
                    <option value="en">EN</option>
                    <option value="da">DA</option>
                  </select>
                </div>
                <button className="change-password-button" onClick={openChangePassword} title={t('app.changePassword.buttonTitle')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M12 17a2 2 0 100-4 2 2 0 000 4zm6-8h-1V7a5 5 0 10-10 0v2H6a2 2 0 00-2 2v7a2 2 0 002 2h12a2 2 0 002-2v-7a2 2 0 00-2-2zM9 7a3 3 0 016 0v2H9V7z" fill="currentColor" />
                  </svg>
                </button>
                <button className="logout-button" onClick={logout} title={t('app.logout')}>
                  {t('app.logout')}
                </button>
              </div>
            </div>
          </header>

          <section className="v2-content-area">
            {activeTabConfig?.render ? activeTabConfig.render({ subpage: resolvedActiveSubpage, showInternalNav: false }) : null}
          </section>
        </main>

        {changePasswordModal}
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-left">
            <div className="brand-logo">
              <span className="logo-icon">V</span>
              <div className="brand-text">
                <h1>Void Development</h1>
                <p className="subtitle">{t('app.subtitle')}</p>
              </div>
            </div>
            <p className="tagline">{t('app.tagline')}</p>
          </div>
          <div className="header-actions">
            <div className="user-info">
              <span className="user-avatar">{user?.username?.charAt(0).toUpperCase()}</span>
              <div className="user-details">
                <span className="user-name">{user?.display_name || user?.username}</span>
                <span className="user-role">{formatRole(user?.role)}</span>
              </div>
            </div>
            {availableSites.length > 1 && (
              <select className="site-select-header" value={siteId} onChange={(e) => { setSiteId(e.target.value); localStorage.setItem('adminSiteId', e.target.value); }} title={t('I18n.siteId') || 'Site'}>
                <option value="default">Default</option>
                {availableSites.map(s => (
                  <option key={s.site_id} value={s.site_id}>{s.name || s.site_id}</option>
                ))}
              </select>
            )}
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              title={t('app.switchTheme', { theme: theme === 'dark' ? t('app.light') : t('app.dark') })}
            >
              {theme === 'dark' ? 'Light' : 'Dark'}
            </button>
            {/* UI version toggle removed; V2 only */}
            <div className="language-switcher">
              <select
                className="language-select-header"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                title="Language"
                aria-label="Language"
              >
                <option value="en">EN</option>
                <option value="da">DA</option>
              </select>
            </div>
            <button className="change-password-button" onClick={openChangePassword} title={t('app.changePassword.buttonTitle')}>
              
            </button>
            <button className="logout-button" onClick={logout} title={t('app.logout')}>
              {t('app.logout')}
            </button>
          </div>
        </div>
      </header>
      
      <nav className="tab-navigation">
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${resolvedActiveTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              setActiveSubpageByTab((prev) => {
                if (prev[tab.id]) return prev;
                const firstSubpage = getVisibleSubpages(tab)[0]?.id;
                return {
                  ...prev,
                  [tab.id]: firstSubpage
                };
              });
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="content-area">
        {activeTabConfig?.render ? activeTabConfig.render({ subpage: resolvedActiveSubpage, showInternalNav: true }) : null}
      </div>

      {changePasswordModal}
    </div>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
